import React, { useEffect, useState } from 'react';
import Icon from './icons';
import { getUp, getUpKey, getTxt, mutStore } from './stepcommon';
import { coVal, coUploadId } from './collect';
import { fmtComma, onlyDigits } from './bondcalc';
import { fileUrl, uploadPreview } from './api';
import type { Project, CoCard, Collect } from './types';

const SLOT_INFO: Record<'biz' | 'fin' | 'reg', { mapKey: string; title: string }> = {
  biz: { mapKey: 'co_biz', title: '사업자등록증' },
  fin: { mapKey: 'co_fin', title: '최근연도 재무제표' },
  reg: { mapKey: 'co_reg', title: '법인 등기부등본' },
};

interface Props {
  p: Project;
  co: CoCard;
  collect: Collect;
  slot: 'biz' | 'fin' | 'reg';
  fieldKey: string;
  fieldLabel: string;
  fieldKind: 'text' | 'num' | 'date';
  split?: boolean;
  onPatch: (patch: Partial<Project>) => void;
  onClose: () => void;
}

export default function BondDocModal({ p, co, collect, slot, fieldKey, fieldLabel, fieldKind, split, onPatch, onClose }: Props) {
  const info = SLOT_INFO[slot];
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [zoom, setZoom] = useState(1);

  const upName = getUp(p, 's8', co.id + '_' + slot);
  const upKey = getUpKey(p, 's8', co.id + '_' + slot);
  const linked = upName ? '' : coVal(collect, co, info.mapKey);
  const fileName = upName || linked || '';

  useEffect(() => {
    const k = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [onClose]);

  useEffect(() => {
    let alive = true;
    setUrl(''); setZoom(1);
    const run = async () => {
      try {
        if (upKey) { const u = await fileUrl(upKey, upName || 'file', true); if (alive) setUrl(u || ''); }
        else if (linked) { const id = coUploadId(collect, linked); if (id) { const r = await uploadPreview(id); if (alive) setUrl(r.url || ''); } }
      } catch { /* 미리보기 실패 시 값 입력은 계속 가능 */ } finally { if (alive) setLoading(false); }
    };
    if (upKey || linked) { setLoading(true); void run(); } else setLoading(false);
    return () => { alive = false; };
  }, [upKey, linked, upName, collect]);

  const setTxt = (key: string, val: string) =>
    onPatch(mutStore(p, 's8', (st) => { st.txt = st.txt || {}; st.txt[co.id + '_b_' + key] = val; }));

  // 자본금/준비금 분리 입력 → 합산이 capital(자본금+준비금 총액)에 반영
  const cap = getTxt(p, 's8', co.id + '_b_capCapital');
  const rsv = getTxt(p, 's8', co.id + '_b_capReserve');
  const sum = String(parseInt(onlyDigits(cap) || '0', 10) + parseInt(onlyDigits(rsv) || '0', 10));
  const setSplit = (which: 'capCapital' | 'capReserve', v: string) => {
    const raw = onlyDigits(v);
    onPatch(mutStore(p, 's8', (st) => {
      st.txt = st.txt || {};
      st.txt[co.id + '_b_' + which] = raw;
      const a = parseInt(onlyDigits(st.txt[co.id + '_b_capCapital'] || '') || '0', 10);
      const b = parseInt(onlyDigits(st.txt[co.id + '_b_capReserve'] || '') || '0', 10);
      st.txt[co.id + '_b_capital'] = String(a + b);
    }));
  };

  const single = getTxt(p, 's8', co.id + '_b_' + fieldKey);
  const setSingle = (v: string) => setTxt(fieldKey, fieldKind === 'num' ? onlyDigits(v) : v);

  const isImg = /\.(jpe?g|png|gif|webp|bmp|svg)$/i.test(fileName);
  const zbtn: React.CSSProperties = { background: '#fff', border: '1px solid #c5cbd6', borderRadius: 6, padding: '2px 9px', cursor: 'pointer', fontSize: 13, lineHeight: 1.4, color: '#1a2438' };

  return (
    <div className="ovm">
      <div className="ovbox docbox" style={url ? { maxWidth: 960, width: '94%' } : undefined}>
        <div className="ovh"><b>🏢 {co.name || '법인'} · {info.title}</b><button className="povx" onClick={onClose}><Icon n="x" /> 닫기</button></div>
        <div className="docprev">
          {fileName ? (
            url ? (
              isImg ? (
                <div style={{ position: 'relative', width: '100%', height: '54vh', overflow: 'auto', background: '#525659', borderRadius: 8 }}>
                  <div style={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 6, zIndex: 2 }}>
                    <button type="button" onClick={() => setZoom((z) => Math.max(1, Math.round((z - 0.25) * 100) / 100))} style={zbtn}>－</button>
                    <button type="button" onClick={() => setZoom(1)} style={zbtn}>{Math.round(zoom * 100)}%</button>
                    <button type="button" onClick={() => setZoom((z) => Math.min(5, Math.round((z + 0.25) * 100) / 100))} style={zbtn}>＋</button>
                  </div>
                  <div style={{ minHeight: '100%', display: 'flex', alignItems: zoom > 1 ? 'flex-start' : 'center', justifyContent: zoom > 1 ? 'flex-start' : 'center', padding: 8, boxSizing: 'border-box' }}>
                    <img src={url} alt={fileName} onClick={() => setZoom((z) => (z > 1 ? 1 : 2))} style={{ width: zoom === 1 ? 'auto' : (zoom * 100) + '%', maxWidth: zoom === 1 ? '100%' : 'none', maxHeight: zoom === 1 ? '100%' : 'none', objectFit: 'contain', cursor: zoom > 1 ? 'zoom-out' : 'zoom-in' }} />
                  </div>
                </div>
              ) : (
                <iframe title={fileName} src={url} style={{ width: '100%', height: '54vh', border: '1px solid #e5e9f2', borderRadius: 8, background: '#525659' }} />
              )
            ) : (
              <div className="docimg">📄<div className="docfn">{fileName}</div><div className="dochint">{loading ? '미리보기 불러오는 중…' : '미리보기를 불러올 수 없습니다'}</div></div>
            )
          ) : (
            <div className="docnone">{info.title} 미업로드 · &apos;직접 업로드&apos; 또는 1단계 연동 후 열람 가능 (값은 아래에서 입력할 수 있습니다)</div>
          )}
        </div>
        {split ? (
          <div className="docfield">
            <label>자본금 / 준비금 입력 (재무제표 보며) · 합산이 &apos;자본금+준비금 총액&apos;에 자동 반영</label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <input style={{ flex: 1, minWidth: 130 }} inputMode="numeric" value={fmtComma(cap)} onChange={(e) => setSplit('capCapital', e.target.value)} placeholder="자본금 (원)" autoFocus />
              <input style={{ flex: 1, minWidth: 130 }} inputMode="numeric" value={fmtComma(rsv)} onChange={(e) => setSplit('capReserve', e.target.value)} placeholder="준비금 (원)" />
            </div>
            <div style={{ marginTop: 8, fontSize: 13, color: '#1a2438' }}>합계 (자본금+준비금 총액): <b>{fmtComma(sum)} 원</b></div>
          </div>
        ) : (
          <div className="docfield">
            <label>{fieldLabel} 입력 (미리보기 보며)</label>
            <input value={fieldKind === 'num' ? fmtComma(single) : single} inputMode={fieldKind === 'num' ? 'numeric' : undefined} onChange={(e) => setSingle(e.target.value)} placeholder={fieldLabel} autoFocus />
          </div>
        )}
        <div className="docnote">바깥을 클릭해도 닫히지 않습니다 · [닫기]를 눌러 저장하고 닫으세요</div>
      </div>
    </div>
  );
}
